import { useEffect, useState } from 'react';
import { useForm, Controller } from "react-hook-form";
import SpinnerLoader from '../../layout/spinner-loader';
import { FilePath, FormatMoney } from '../../../services/utilities';
import { Box, Chip, FormHelperText } from "@mui/material";
import NumberSpinner from '../../number-spinner.js';
import './merchone-item-detail.scss';

const SIZE_UPCHARGE_STEP = 1.50;

const getSizeUpCharge = (size) => {
    if (!size) return 0;
    const s = String(size).trim().toUpperCase();

    if (s === "XL" || s === "L" || s === "M" || s === "S" || s === "XS") return 0;

    const match = s.match(/^(X{2,})L$/); 
    if (!match) return 0;

    const xCount = match[1].length;   
    return (xCount - 1) * SIZE_UPCHARGE_STEP;
};

const MerchOneItemDetail = ({ merchOneProduct, cart, setCart }) => {
    const [loading, setLoading] = useState(true);
    const [productToView, setProductToView] = useState(null);
    const { handleSubmit, watch, setValue, reset, control } = useForm({ defaultValues: { quantity: 1, size: null, color: null, companyLogo: 'TitleOne' } });
    const quantityWatcher = watch("quantity");
    const sizeWatcher = watch("size");
    const sizeUpCharge = getSizeUpCharge(sizeWatcher);
    const basePrice = productToView?.price ?? 0;
    const unitPrice = basePrice + sizeUpCharge;
    const itemTotal = unitPrice * (quantityWatcher ?? 1);
   
    useEffect(() => {
        if (merchOneProduct) {
            reset();
            setProductToView(merchOneProduct);
            setLoading(false);
        }
    }, [merchOneProduct]);

    useEffect(() => {
        if (!productToView) {
            reset();
            return;
        }
        if (productToView.colorOptions.split(',').length === 1) setValue('color', productToView.colorOptions);
        if (productToView.sizeOptions.split(',').length === 1) setValue('size', productToView.sizeOptions);
    }, [productToView]);

    const updateCart = (data) => {
        const qty = data.quantity ?? 1;
        const computedUpCharge = getSizeUpCharge(data.size);

        const newCartItem = {
            merchOneProductId: productToView.merchOneProductId,
            productNumber: productToView.productNumber,
            costPerItem: productToView.price,
            sizeUpCharge: computedUpCharge,
            lineItemAmount: (productToView.price + computedUpCharge) * qty,
            description: productToView.description,
            logoLocationId: productToView.logoLocationId,
            inHouseFulfillment: productToView.inHouseFulfillment,
            imageUrl: FilePath(`/images/merchoneproducts/${productToView.thumbnail}`),
            ...data
        };

        setCart((prev) => {
            const existing = prev.find((item) =>
                item.merchOneProductId === newCartItem.merchOneProductId 
                && item.size === newCartItem.size
                && item.color === newCartItem.color 
                && item.companyLogo === newCartItem.companyLogo
            );
            if (existing) {
                const newQty = Number(existing.quantity ?? 0) + Number(qty ?? 0);
                const perUnit = Number(existing.costPerItem ?? 0) + Number(existing.sizeUpCharge ?? 0);

                return prev.map((item) =>
                    item.merchOneProductId === newCartItem.merchOneProductId 
                        && item.size === newCartItem.size 
                        && item.color === newCartItem.color 
                        && item.companyLogo === newCartItem.companyLogo
                        ? {...item, quantity: newQty, lineItemAmount: perUnit * newQty }
                        : item
                );
            }

            return [
                ...prev,
                newCartItem];
        });
    };

    return (
        <div>
            {loading && <SpinnerLoader />}
            {!loading && productToView &&
                <div className="modal-content">
                    <form onSubmit={handleSubmit(updateCart)}>
                        <div className="modal-header">
                            <h4 className="modal-title">{productToView.description}</h4>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => window.history.pushState({}, undefined, "/merchone/") } ></button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex flex-wrap">
                                <div className="pe-0 pe-md-4">
                                    {productToView.detailImage && <img key={productToView.merchOneProductId} src={FilePath('/images/merchoneproducts/' + productToView.detailImage)} width="650" alt={`${productToView.description}`} />}
                                </div>
                                <div className="store-item-options-column">
                                    <fieldset className="mb-3">
                                        <h5 className="mb-2">Brand Logo</h5>
                                        <Controller
                                            name="companyLogo"
                                            control={control}
                                            rules={{ required: "Please pick a brand" }}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                        {['TitleOne', 'Real1031', 'TitleOne 1031'].map((s) => {
                                                            const selected = field.value === s;
                                                            return (
                                                                <Chip
                                                                    size="large"
                                                                    key={s}
                                                                    label={s}
                                                                    clickable
                                                                    onClick={() => field.onChange(s)}  // set single value
                                                                    variant={selected ? "filled" : "outlined"}
                                                                    color={selected ? "primary" : "default"}
                                                                />
                                                            );
                                                        })}
                                                    </Box>

                                                    {fieldState.error?.message && (
                                                        <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </fieldset>

                                    <fieldset className="mb-3">
                                        <h5 className="mb-2">Color</h5>
                                        <Controller
                                            name="color"
                                            control={control}
                                            rules={{ required: "Please pick a color" }}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                        {productToView.colorOptions?.split(',').map((s) => {
                                                            const selected = field.value === s;
                                                            return (
                                                                <Chip
                                                                    size="large"
                                                                    key={s}
                                                                    label={s}
                                                                    clickable
                                                                    onClick={() => field.onChange(s)}  // set single value
                                                                    variant={selected ? "filled" : "outlined"}
                                                                    color={selected ? "primary" : "default"}
                                                                />
                                                            );
                                                        })}
                                                    </Box>

                                                    {fieldState.error?.message && (
                                                        <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </>

                                            )}
                                        />
                                    </fieldset>

                                    <fieldset className="mb-3">
                                        <h5 className="mb-2">Size</h5>
                                        <div className="flex flex-wrap">
                                            <Controller
                                                name="size"
                                                control={control}
                                                rules={{ required: "Please pick a size" }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                            {productToView.sizeOptions?.split(',').map((s) => {
                                                                const selected = field.value === s;
                                                                return (
                                                                    <Chip
                                                                        size="large"
                                                                        key={s}
                                                                        label={s}
                                                                        clickable
                                                                        onClick={() => field.onChange(s)}  // set single value
                                                                        variant={selected ? "filled" : "outlined"}
                                                                        color={selected ? "primary" : "default"}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>

                                                        {fieldState.error?.message && (
                                                            <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                                        )}
                                                    </>

                                                )}
                                            />
                                        </div>
                                        <div className="mt-2"><a href={FilePath('/documents/merchone-sizing-guide.pdf')} alt="Store Sizing Guide" target="_blank" rel="noopener noreferrer">Sizing Guide</a></div>
                                    </fieldset>
                                    <fieldset className="mb-3">
                                        <h5 className="mb-2" id="quantityLabel">Quantity</h5>
                                        <Controller
                                            name="quantity"
                                            control={control}
                                            rules={{ required: "Please pick a quantity" }}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <NumberSpinner
                                                        {...field}
                                                        aria-label="quantityLabel"
                                                        min={1} max={100}
                                                        size="xs"
                                                        onValueChange={(next) => {
                                                            field.onChange(next ?? 1); // fall back to min (or 0)
                                                        }}
                                                        error={!!fieldState.error}
                                                    />
                                                    {fieldState.error?.message && (
                                                        <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </>
                                            )}
                                        />

                                    </fieldset>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <span className="pe-3 h4">
                                Item total: {FormatMoney(itemTotal)}
                                {sizeUpCharge > 0 && (
                                    <span className="ms-2 text-muted">
                                        (includes {FormatMoney(sizeUpCharge)} size upcharge)
                                    </span>
                                )}
                            </span> <button type="submit" className="btn btn-primary">Add to Cart</button>
                        </div>
                    </form>
                </div>
            }
        </div>
    );
}

export default MerchOneItemDetail;
